import React, { useMemo, useState, useEffect, useRef } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useToast } from '../context/ToastContext';
import {
    getSubAgentDefinition,
    updateSubAgentDefinition
} from '../services/agentApi';
import {
    X,
    Loader,
    ArrowLeft,
    Save,
    Edit3,
    CheckCircle2,
    Shield,
    FileText,
    Info
} from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';



function splitFrontmatter(markdown) {
    const text = markdown || '';
    if (!text.startsWith('---')) {
        return { hasFrontmatter: false, frontmatter: '', body: text };
    }
    const lines = text.split('\n');
    if (lines.length < 3) return { hasFrontmatter: false, frontmatter: '', body: text };
    // first line is ---
    let endIdx = -1;
    for (let i = 1; i < lines.length; i++) {
        if (lines[i].trim() === '---') {
            endIdx = i;
            break;
        }
    }
    if (endIdx === -1) return { hasFrontmatter: false, frontmatter: '', body: text };
    const fm = lines.slice(1, endIdx).join('\n');
    const body = lines.slice(endIdx + 1).join('\n');
    return { hasFrontmatter: true, frontmatter: fm, body };
}

function parseSimpleFrontmatter(frontmatterText) {
    // Minimal parser supporting:
    // - key: value
    // - key:
    //     - item
    const lines = (frontmatterText || '').split('\n');
    const data = {};
    let i = 0;
    while (i < lines.length) {
        const raw = lines[i];
        const line = raw.trim();
        i++;
        if (!line || line.startsWith('#')) continue;
        const m = /^([A-Za-z0-9_]+)\s*:\s*(.*)$/.exec(line);
        if (!m) continue;
        const key = m[1];
        const rest = m[2] ?? '';
        if (rest === '') {
            // maybe a list
            const items = [];
            while (i < lines.length) {
                const l = lines[i];
                const t = l.trim();
                if (!t) {
                    i++;
                    continue;
                }
                if (/^[A-Za-z0-9_]+\s*:/.test(t)) break;
                const li = /^-\s+(.*)$/.exec(t);
                if (li) items.push(li[1]);
                i++;
            }
            data[key] = items;
        } else {
            data[key] = rest.replace(/^"(.*)"$/, '$1');
        }
    }
    return data;
}

function parseTableFromBullet(text) {
    const raw = (text || '').trim();
    if (!raw) return null;
    const bold = /^\*\*([A-Za-z_][A-Za-z0-9_]*)\*\*/.exec(raw);
    if (bold) return bold[1];
    const backtick = /^`([A-Za-z_][A-Za-z0-9_]*)`/.exec(raw);
    if (backtick) return backtick[1];
    const plain = /^([A-Za-z_][A-Za-z0-9_]*)/.exec(raw);
    return plain ? plain[1] : null;
}

function extractAllowedTablesFromBody(body) {
    const text = body || '';
    const re = /^#{1,6}\s*Allowed\s+tables\s*$/gim;
    const m = re.exec(text);
    if (!m) return [];
    const tail = text.slice(m.index + m[0].length).replace(/^\n+/, '');
    const tables = [];
    const seen = new Set();
    for (const line of tail.split('\n')) {
        if (/^#{1,6}\s+/.test(line)) break;
        const bm = /^\s*-\s+(.*)$/.exec(line);
        if (!bm) continue;
        const tid = parseTableFromBullet(bm[1]);
        if (tid && !seen.has(tid)) {
            seen.add(tid);
            tables.push(tid);
        }
    }
    return tables;
}

function buildFrontmatter(existingFrontmatterText, { description }) {
    const existing = parseSimpleFrontmatter(existingFrontmatterText || '');
    const preserved = {};
    Object.keys(existing).forEach((k) => {
        if (
            k === 'description' ||
            k === 'tables' ||
            k === 'allowed_tables' ||
            k === 'tools'
        ) {
            return;
        }
        preserved[k] = existing[k];
    });

    const desc = (description || '').trim();
    const lines = [];
    lines.push('---');
    Object.keys(preserved).forEach((k) => {
        const v = preserved[k];
        if (Array.isArray(v)) {
            lines.push(`${k}:`);
            v.forEach((it) => lines.push(`  - ${it}`));
        } else {
            lines.push(`${k}: ${v ?? ''}`);
        }
    });
    lines.push(`description: ${desc}`);
    lines.push('---');
    return lines.join('\n');
}

function mergeMarkdown(markdown, { description }) {
    const { frontmatter, body } = splitFrontmatter(markdown || '');
    const fm = buildFrontmatter(frontmatter, { description });
    const cleanedBody = (body || '').replace(/^\n+/, '');
    return `${fm}\n\n${cleanedBody}`.trimEnd() + '\n';
}

/**
 * SubAgentEdit - Page for editing sub-agent settings.
 * Matches the design in mark.md image exactly.
 */
export const SubAgentEdit = () => {
    const { t } = useTranslation(['pages-misc-4', 'common']);
    const { agentId } = useParams();
    const navigate = useNavigate();
    const { showToast } = useToast();

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [isEditMode, setIsEditMode] = useState(false);
    const mainTextareaRef = useRef(null);
    const descTextareaRef = useRef(null);

    const [meta, setMeta] = useState({ description: '' });

    const [agentData, setAgentData] = useState({
        id: null,
        name: '',
        description: '',
        focus: '',
        status: 'idle',
        agent_slug: null,
        agent_markdown: '',
        tables: [],
        database_connection_id: null
    });

    // Adaptive height for main textarea
    useEffect(() => {
        if (isEditMode && mainTextareaRef.current) {
            mainTextareaRef.current.style.height = 'auto';
            mainTextareaRef.current.style.height = `${mainTextareaRef.current.scrollHeight}px`;
        }
    }, [isEditMode, agentData.agent_markdown]);

    // Adaptive height for description
    useEffect(() => {
        if (isEditMode && descTextareaRef.current) {
            descTextareaRef.current.style.height = 'auto';
            descTextareaRef.current.style.height = `${descTextareaRef.current.scrollHeight}px`;
        }
    }, [isEditMode, meta.description]);



    const parsedMeta = useMemo(() => {
        const { frontmatter, body } = splitFrontmatter(agentData.agent_markdown || '');
        const fm = parseSimpleFrontmatter(frontmatter || '');
        const tables = extractAllowedTablesFromBody(body);
        return {
            description: (fm.description || agentData.description || '').toString(),
            tables
        };
    }, [agentData.agent_markdown, agentData.description]);

    // Fetch sub-agent definition
    const fetchSubAgent = async () => {
        if (!agentId) return;

        setLoading(true);
        try {
            const response = await getSubAgentDefinition(agentId);
            console.log('SubAgent API Response:', response.data);

            if (response.data?.status === 'success') {
                const data = response.data.data;
                console.log('SubAgent Data:', data);
                console.log('Database Connection ID:', data.database_connection_id);
                console.log('Tables:', data.tables);

                setAgentData({
                    ...data,
                    database_connection_id: data.database_connection_id || null,
                    tables: data.tables || [],
                    description: data.description || '',
                    agent_slug: data.agent_slug || null,
                    agent_markdown: data.agent_markdown || ''
                });
                // sync UI meta from markdown
                const { frontmatter } = splitFrontmatter(data.agent_markdown || '');
                const fm = parseSimpleFrontmatter(frontmatter || '');
                setMeta({
                    description: (fm.description || data.description || '').toString()
                });
                // Keep edit mode disabled by default - user must click "Edit" to enable
                setIsEditMode(false);
            }
        } catch (error) {
            console.error('Failed to fetch sub-agent:', error);
            showToast(t('subAgentEdit.toastLoadFailed'), 'error');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        if (agentId) {
            fetchSubAgent();
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [agentId]);

    // Handle cancel edit - reload original data
    const handleCancelEdit = async () => {
        setIsEditMode(false);
        // Reload original data
        await fetchSubAgent();
    };

    const startEdit = () => {
        setMeta({
            description: parsedMeta.description || ''
        });
        setIsEditMode(true);
    };

    // Handle save
    const handleSave = async () => {
        setSaving(true);
        try {
            const nextMarkdown = mergeMarkdown(agentData.agent_markdown || '', {
                description: meta.description
            });
            const payload = {
                name: agentData.name,
                agent_markdown: nextMarkdown
            };

            const response = await updateSubAgentDefinition(agentId, payload);

            if (response.data?.status === 'success') {
                showToast(t('subAgentEdit.toastUpdated'), 'success');
                fetchSubAgent();
            }
        } catch (error) {
            console.error('Failed to update sub-agent:', error);
            showToast(t('subAgentEdit.toastUpdateFailed'), 'error');
        } finally {
            setSaving(false);
        }
    };

    const handleBack = () => {
        navigate(-1);
    };

    if (loading) {
        return (
            <div className="page-content">
                <div className="flex justify-center py-12">
                    <Loader className="w-8 h-8 animate-spin text-primary-600" />
                </div>
            </div>
        );
    }

    return (
        <div className="page-content">
            {/* Header */}
            <div className="mb-6">
                <button
                    onClick={handleBack}
                    className="flex items-center text-gray-600 hover:text-gray-900 mb-4"
                >
                    <ArrowLeft className="w-4 h-4 mr-2" />
                    {t('subAgentEdit.back')}
                </button>

                <div className="flex items-center justify-between">
                    <div>
                        <h1 className="text-2xl font-semibold text-gray-900">
                            {isEditMode ? t('subAgentEdit.editAgentTitle') : t('subAgentEdit.settingSuffix', { name: agentData.name })}
                        </h1>
                        <p className="text-sm text-gray-500 mt-1">
                            {isEditMode ? t('subAgentEdit.editSubtitle') : (agentData.focus || '')}
                        </p>
                    </div>
                    <div className="flex items-center space-x-3">
                        {isEditMode ? (
                            <>
                                <button
                                    onClick={handleCancelEdit}
                                    className="px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all bg-white border border-gray-200 text-gray-700 hover:bg-gray-50"
                                >
                                    <X className="w-4 h-4" />
                                    <span>{t('common:cancel')}</span>
                                </button>
                                <button
                                    onClick={handleSave}
                                    disabled={saving}
                                    className="px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all bg-primary-600 text-white hover:bg-primary-700 disabled:bg-gray-300 disabled:text-gray-500"
                                >
                                    {saving ? <Loader className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                                    <span>{t('common:saveChanges')}</span>
                                </button>
                            </>
                        ) : (
                            <button
                                onClick={startEdit}
                                className="px-4 py-2 rounded-lg font-medium flex items-center space-x-2 transition-all bg-primary-600 text-white hover:bg-primary-700 shadow-sm shadow-primary-200"
                            >
                                <Edit3 className="w-4 h-4" />
                                <span>{t('subAgentEdit.editAgent')}</span>
                            </button>
                        )}
                    </div>
                </div>
            </div>

            {isEditMode ? (
                <div className="space-y-8 max-w-6xl mx-auto">
                    {/* Agent Configuration Card */}
                    <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="bg-gray-50/50 px-8 py-5 border-b border-gray-100">
                            <h3 className="text-[10px] font-bold text-gray-900 uppercase tracking-[0.2em]">{t('subAgentEdit.agentConfiguration')}</h3>
                        </div>
                        <div className="p-8">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5">{t('subAgentEdit.agentName')}</label>
                                    <input
                                        type="text"
                                        value={agentData.name || ''}
                                        onChange={(e) => setAgentData(prev => ({ ...prev, name: e.target.value }))}
                                        className="w-full px-5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all bg-gray-50/30"
                                        placeholder={t('subAgentEdit.agentNamePlaceholder')}
                                    />
                                </div>
                                <div>
                                    <label className="block text-[10px] font-bold text-gray-400 uppercase tracking-widest mb-2.5">{t('common:description')}</label>
                                    <textarea
                                        ref={descTextareaRef}
                                        value={meta.description}
                                        onChange={(e) => setMeta(prev => ({ ...prev, description: e.target.value }))}
                                        className="w-full px-5 py-3 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500/20 transition-all bg-gray-50/30 resize-none overflow-hidden min-h-[45px] leading-relaxed"
                                        placeholder={t('subAgentEdit.descriptionPlaceholder')}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Agent Instructions Card */}
                    <div className="bg-white rounded-3xl border border-gray-200 overflow-hidden shadow-sm">
                        <div className="bg-gray-50/50 px-8 py-4 border-b border-gray-100 flex items-center justify-between">
                            <h3 className="text-[10px] font-bold text-gray-900 uppercase tracking-[0.2em]">{t('subAgentEdit.agentInstructions')}</h3>
                            <div className="flex items-center space-x-1">
                                <span className="text-[9px] text-gray-400 font-mono bg-gray-200/50 px-2 py-1 rounded">.claude/agents/{agentData.agent_slug}.md</span>
                            </div>
                        </div>
                        <div className="p-8">
                            <textarea
                                ref={mainTextareaRef}
                                value={agentData.agent_markdown || ''}
                                onChange={(e) => setAgentData(prev => ({ ...prev, agent_markdown: e.target.value }))}
                                className="w-full font-mono text-xs px-6 py-5 border border-gray-200 rounded-2xl focus:outline-none focus:ring-2 focus:ring-primary-500/20 resize-none bg-gray-50/30 leading-relaxed shadow-inner overflow-hidden min-h-[500px]"
                            />
                            
                            <div className="mt-6 flex items-center justify-between px-2">
                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-[0.2em]">
                                    {t('subAgentEdit.markdownSupport')}
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            ) : (
                <div className="space-y-8 max-w-5xl mx-auto">
                    {/* Main Content Area */}
                    <div className="space-y-8">
                        <div className="bg-white rounded-3xl border border-gray-200 shadow-sm overflow-hidden group">
                            <div className="bg-gray-50/50 px-8 py-5 border-b border-gray-100 flex items-center justify-between">
                                <h3 className="text-xs font-bold text-gray-900 flex items-center uppercase tracking-widest">
                                    <FileText className="w-4 h-4 mr-2.5 text-primary-500" />
                                    {t('subAgentEdit.agentInstructionsView')}
                                </h3>
                                <div className="flex items-center space-x-2">
                                    <span className="px-2 py-1 bg-primary-50 text-primary-600 text-[10px] font-bold rounded-md border border-primary-100 uppercase tracking-tight">
                                        {t('subAgentEdit.markdownRendered')}
                                    </span>
                                </div>
                            </div>

                            <div className="p-10">
                                <div className="prose prose-slate max-w-none
                                    prose-headings:text-gray-900 prose-headings:font-bold prose-headings:tracking-tight
                                    prose-p:text-gray-600 prose-p:leading-relaxed
                                    prose-li:text-gray-600 prose-li:leading-relaxed
                                    prose-strong:text-gray-900 prose-strong:font-bold
                                    prose-code:text-primary-600 prose-code:bg-primary-50 prose-code:px-1.5 prose-code:py-0.5 prose-code:rounded prose-code:before:content-none prose-code:after:content-none
                                    prose-pre:bg-gray-900 prose-pre:text-gray-100 prose-pre:rounded-2xl prose-pre:p-6
                                    prose-blockquote:border-l-primary-500 prose-blockquote:bg-gray-50 prose-blockquote:py-1 prose-blockquote:px-6 prose-blockquote:rounded-r-2xl
                                ">
                                    <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                        {splitFrontmatter(agentData.agent_markdown).body || t('subAgentEdit.noInstructions')}
                                    </ReactMarkdown>
                                </div>
                            </div>
                        </div>

                        {/* Additional Info Footer */}
                        <div className="flex items-center justify-between px-4 text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                            <div className="flex items-center">
                                <Info className="w-3.5 h-3.5 mr-2" />
                                <span>{t('subAgentEdit.contentDerivedFrom', { slug: agentData.agent_slug })}</span>
                            </div>
                            <div className="flex items-center space-x-4">
                                <span className="flex items-center"><CheckCircle2 className="w-3 h-3 mr-1.5 text-green-500" /> {t('subAgentEdit.systemReady')}</span>
                                <span className="flex items-center"><Shield className="w-3 h-3 mr-1.5 text-primary-500" /> {t('subAgentEdit.guardrailsActive')}</span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};
